import { useEffect, useMemo, useState } from 'react';
import { loadTakiBuildStamp } from '@taki/runtime-config';
import type { TakiBuildStamp } from '@taki/runtime-config';

/**
 * Lecture du tampon de build (`/taki-build.json`), cote React.
 *
 * PERMANENT, et c'est important : ce branchement a d'abord ete ecrit pour la
 * ligne de version du menu DEV, mais la bande "app deprecated" (`DeprecatedBand`)
 * le consomme aussi, et elle, elle existe en PRODUCTION. Le fichier vit donc a
 * la racine du shell et pas dans `dev/` : retirer la greffe DEV ne doit pas
 * emporter la bande deprecated avec elle.
 *
 * Le chargeur lui-meme (le fetch, les erreurs typees, la verification du type de
 * contenu) vit dans `@taki/runtime-config` : voir `load-build-stamp.ts`. Ici, on
 * ne fait que l'etat React, le cache et les quatre cas.
 *
 * POURQUOI UN CACHE DE MODULE : la ligne de version est montee dans le layout
 * racine du routeur, donc elle traverse chaque navigation. Sans cache, chaque
 * changement d'app relancerait un fetch pour une information qui ne change pas
 * pendant la vie de l'onglet. Un rechargement de page repart de zero, ce qui
 * suffit : apres un `git checkout`, on recharge.
 *
 * AUCUN APPEL A /__taki/dev/git ICI, JAMAIS. Cette route du serveur de dev scanne
 * le disque (profondeur 3, jusqu'a 40 depots, budget de 8 secondes) : acceptable
 * pour un panneau qu'on ouvre a la demande, jamais pour une bande permanente
 * montee a chaque chargement de page. La branche et le sha viennent du tampon,
 * qui les a lus UNE fois, au demarrage du serveur ou au build.
 */

/**
 * Les quatre etats de la lecture, distincts a dessein.
 *
 * `missing` n'est PAS une erreur : un deploiement fabrique avant l'arrivee du
 * tampon n'en a pas, et l'app doit demarrer normalement. `error` couvre le seul
 * cas anormal : un tampon present mais illisible.
 */
export type BuildStampState =
  | { kind: 'loading' }
  | { kind: 'found'; stamp: TakiBuildStamp }
  | { kind: 'missing'; reason: string }
  | { kind: 'error'; message: string };

/** Cache de module : une seule lecture par chargement de page. */
let pending: Promise<BuildStampState> | null = null;

function loadOnce(): Promise<BuildStampState> {
  if (pending != null) return pending;
  pending = loadTakiBuildStamp().then(
    (outcome): BuildStampState =>
      outcome.found
        ? { kind: 'found', stamp: outcome.stamp }
        : { kind: 'missing', reason: outcome.reason },
    (cause: unknown): BuildStampState => ({
      kind: 'error',
      message: cause instanceof Error ? cause.message : 'Tampon de build illisible.',
    }),
  );
  return pending;
}

/**
 * Lit le tampon une fois et rend l'etat courant. Le cache de module fait qu'un
 * seul fetch a lieu, meme quand deux bandes le consomment en meme temps.
 *
 * Pas de bouton "Recharger", pas de nouvelle tentative automatique : le tampon
 * est fige a la fabrication, le relire ne donnerait jamais autre chose.
 */
export function useBuildStamp(): BuildStampState {
  const [state, setState] = useState<BuildStampState>({ kind: 'loading' });

  useEffect(() => {
    let alive = true;
    void loadOnce().then((next) => {
      if (alive) setState(next);
    });
    return () => {
      alive = false;
    };
  }, []);

  return state;
}

/* -------------------------------------------------------------------------- */
/* Mise en forme de la version (pur)                                           */
/* -------------------------------------------------------------------------- */

/** Texte affiche quand une information n'a pas pu etre lue. Jamais un 0.0.0. */
export const UNKNOWN_VERSION = 'version inconnue';

/**
 * Version de plateforme AFFICHEE : du CalVer, majeur fige a 26, stocke en semver
 * valide (`26.32.0`) et affiche `26.32`. Le troisieme chiffre est reserve aux
 * correctifs poses sur une livraison deja deployee : a zero, il n'apporte rien a
 * l'oeil, donc il ne s'affiche pas ; non nul, il compte et il s'affiche.
 *
 * Une valeur qui n'est pas un semver a trois nombres est rendue TELLE QUELLE :
 * mieux vaut afficher une chaine inattendue que la tronquer en silence.
 *
 * ELLE VIT ICI POUR LA MEME RAISON QUE LE HOOK CI-DESSUS. Elle a ete ecrite pour
 * la ligne de version du menu DEV (`dev/versionLine.ts`), mais l'ecran "Mon
 * compte" l'affiche desormais en PRODUCTION : retirer la greffe DEV ne doit pas
 * emporter la mise en forme de la version avec elle.
 */
export function formatPlatformVersion(version: string | null): string {
  if (version == null || version.length === 0) return UNKNOWN_VERSION;
  const parts = /^(\d+)\.(\d+)\.(\d+)$/.exec(version);
  if (parts == null) return version;
  const [, major, minor, patch] = parts;
  if (major == null || minor == null || patch == null) return version;
  return patch === '0' ? `${major}.${minor}` : version;
}

/* -------------------------------------------------------------------------- */
/* Les apps deprecated                                                        */
/* -------------------------------------------------------------------------- */

/**
 * L'ETAT DEPRECATED DES APPS, EN TROIS BRANCHES, et jamais deux.
 *
 * D'OU VIENT LA VERITE : la fiche `taki.app.json` porte `deprecated`, le tampon
 * de build (`/taki-build.json`) l'y recopie, et ce hook le lit. UNE source, deja
 * en production, deja consommee par la bande `DeprecatedBand`. Recopier les ids
 * dans un registre ou dans un type public en ferait un deuxieme endroit ou la
 * verite est ecrite, donc un endroit qui divergera.
 *
 * POURQUOI `loading` ET `unknown` N'EXCLUENT PERSONNE. Un deploiement sans
 * tampon (un vieux build, un serveur qui ne le sert pas) ferait disparaitre
 * TOUTES les apps du dock, du menu et des Parametres, et il n'y aurait plus
 * aucun ecran pour defaire ce reglage. Une icone en trop pendant une frame est
 * un desagrement ; une app vivante disparue sans recours est une panne.
 *
 * IL VIT DANS CE FICHIER PERMANENT, et pas dans la greffe DEV ou il est ne :
 * masquer une app morte est un comportement de PRODUCTION. Le jour du
 * `rm -rf packages/shell/src/dev/`, ce hook doit rester.
 */
export type DeprecatedAppsState =
  | { kind: 'loading' }
  | { kind: 'unknown'; reason: string }
  | { kind: 'known'; ids: ReadonlySet<string> };

/**
 * Repli PARTAGE : "on ne sait pas" se comporte comme "aucune app deprecated".
 *
 * Une seule instance, exportee : si chaque appelant ecrivait son propre
 * `new Set()`, il fabriquerait une reference neuve a chaque rendu, et les
 * `useMemo` qui en dependent se recalculeraient sans fin.
 */
export const NO_DEPRECATED_IDS: ReadonlySet<string> = new Set<string>();

/** L'etat deprecated, derive du tampon. PURE : testable sans React. */
export function deprecatedAppsStateOf(stamp: BuildStampState): DeprecatedAppsState {
  if (stamp.kind === 'loading') return { kind: 'loading' };
  if (stamp.kind === 'missing') return { kind: 'unknown', reason: stamp.reason };
  if (stamp.kind === 'error') return { kind: 'unknown', reason: stamp.message };
  return {
    kind: 'known',
    ids: new Set(stamp.stamp.apps.filter((app) => app.deprecated).map((app) => app.id)),
  };
}

/** L'etat deprecated du tampon courant. */
export function useDeprecatedAppIds(): DeprecatedAppsState {
  const stamp = useBuildStamp();
  return useMemo(() => deprecatedAppsStateOf(stamp), [stamp]);
}
